import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Users, 
  TrendingUp, 
  Calendar, 
  Download, 
  LogOut, 
  Search,
  Filter,
  MoreHorizontal,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  Award,
  Shield,
  Eye,
  X,
  Trash2,
  Gift,
  Mail
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentProgress, setStudentProgress] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [idVerifications, setIdVerifications] = useState([]);
  const [paymentConfirmations, setPaymentConfirmations] = useState([]);
  const [authorOrders, setAuthorOrders] = useState([]);
  const [leadSubscribers, setLeadSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCourse, setFilterCourse] = useState('all');
  const [reviewingId, setReviewingId] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, studentsResponse, progressResponse, certificatesResponse, idVerificationsResponse, paymentConfirmationsResponse, authorOrdersResponse, leadSubscribersResponse] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/students`),
        axios.get(`${API}/admin/student-progress`),
        axios.get(`${API}/admin/certificates`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/id-verifications`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/payment-confirmations`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/author-orders`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/lead-subscribers`).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsResponse.data);
      setStudents(studentsResponse.data);
      setStudentProgress(progressResponse.data);
      setCertificates(certificatesResponse.data);
      setIdVerifications(idVerificationsResponse.data);
      setPaymentConfirmations(paymentConfirmationsResponse.data);
      setAuthorOrders(authorOrdersResponse.data);
      setLeadSubscribers(leadSubscribersResponse.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const activateCertificate = async (certificateId) => {
    try {
      await axios.post(`${API}/admin/certificate/${certificateId}/activate`);
      toast.success('Certificate activated successfully!');
      fetchDashboardData(); // Refresh data
    } catch (error) {
      toast.error('Failed to activate certificate');
    }
  };

  const markStudentComplete = async (studentId) => {
    try {
      await axios.put(`${API}/admin/students/${studentId}/complete`);
      toast.success('Student marked as completed');
      fetchDashboardData(); // Refresh data
    } catch (error) {
      toast.error('Failed to update student status');
    }
  };

  const updateStudentStatus = async (studentId, newStatus) => {
    try {
      await axios.put(`${API}/admin/students/${studentId}/status`, {
        completion_status: newStatus
      });
      toast.success('Student status updated');
      fetchDashboardData(); // Refresh data
    } catch (error) {
      toast.error('Failed to update student status');
    }
  };

  const deleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${studentName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/students/${studentId}`);
      toast.success('Student deleted successfully');
      fetchDashboardData(); // Refresh data
    } catch (error) {
      toast.error('Failed to delete student');
    }
  };

  const markPaymentReceived = async (studentId, studentName, enrollmentPaid, coursePaid) => {
    // Determine what payments are needed
    let paymentType = 'both';
    if (enrollmentPaid && !coursePaid) {
      paymentType = 'course';
    } else if (!enrollmentPaid && coursePaid) {
      paymentType = 'enrollment';
    } else if (!enrollmentPaid && !coursePaid) {
      paymentType = 'both';
    } else {
      toast.info('Student has already paid for everything');
      return;
    }
    
    const confirmMessage = paymentType === 'both' 
      ? `Mark ${studentName} as fully paid (enrollment + course)?`
      : `Mark ${paymentType} payment as received for ${studentName}?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await axios.post(`${API}/admin/students/${studentId}/mark-paid`, {
        payment_type: paymentType,
        payment_method: 'manual',
        notes: 'Manually marked as paid by admin'
      });
      toast.success('Payment marked as received');
      fetchDashboardData(); // Refresh data
    } catch (error) {
      toast.error('Failed to mark payment as received');
    }
  };

  const verifyPaymentConfirmation = async (confirmationId, action, studentName) => {
    const actionLabels = {
      verify_enrollment: 'verify enrollment payment',
      verify_course: 'verify course payment',
      verify_full: 'verify full payment',
      reject: 'reject payment'
    };

    if (!window.confirm(`Are you sure you want to ${actionLabels[action]} for ${studentName}?`)) {
      return;
    }

    try {
      await axios.put(`${API}/admin/payment-confirmations/${confirmationId}/verify`, null, {
        params: { action }
      });
      toast.success(`Payment ${actionLabels[action]}d successfully`);
      fetchDashboardData(); // Refresh data
    } catch (error) {
      toast.error(`Failed to ${actionLabels[action]}`);
    }
  };

  const reviewIdVerification = async (studentId, status, rejectionReason = '') => {
    try {
      setReviewingId(studentId);
      const reviewData = {
        student_id: studentId,
        status: status,
        rejection_reason: rejectionReason || null
      };
      
      await axios.post(`${API}/admin/id-verification/${studentId}/review`, reviewData);
      toast.success(`ID verification ${status} successfully!`);
      fetchDashboardData(); // Refresh data
    } catch (error) {
      toast.error(`Failed to ${status} ID verification`);
    } finally {
      setReviewingId(null);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Name', 'Email', 'Phone', 'Course Type', 'Start Date', 
      'Enrollment Paid', 'Course Paid', 'Total Paid', 'Status', 'Registration Date'
    ];
    
    const csvData = students.map(student => [
      `${student.first_name} ${student.last_name}`,
      student.email,
      student.phone || '',
      student.course_type || 'Not Selected',
      student.course_start_date || 'Not Selected',
      student.enrollment_paid ? 'Yes' : 'No',
      student.course_paid ? 'Yes' : 'No',
      `£${student.total_paid}`,
      student.completion_status,
      new Date(student.registration_date).toLocaleDateString()
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `students_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusBadge = (student) => {
    if (student.completion_status === 'completed') {
      return <Badge className="status-badge status-completed">Completed</Badge>;
    } else if (student.course_paid && student.course_start_date) {
      return <Badge className="status-badge status-in-progress">In Progress</Badge>;
    } else {
      return <Badge className="status-badge status-enrolled">Enrolled</Badge>;
    }
  };

  const getPaymentStatus = (student) => {
    if (student.enrollment_paid && student.course_paid) {
      return <span className="text-green-600 font-medium">Fully Paid</span>;
    } else if (student.enrollment_paid) {
      return <span className="text-yellow-600 font-medium">Enrollment Only</span>;
    } else {
      return <span className="text-red-600 font-medium">Pending</span>;
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || student.completion_status === filterStatus;
    const matchesCourse = filterCourse === 'all' || student.course_type === filterCourse;
    
    return matchesSearch && matchesStatus && matchesCourse;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="admin-header bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center text-blue-600 hover:text-blue-700">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                  alt="Book Review Specialists Logo"
                  className="w-6 h-6 rounded-full object-cover mr-2"
                />
                <span className="font-semibold">Book Review Specialists</span>
              </Link>
              <span className="text-slate-400">|</span>
              <h1 className="text-xl font-semibold text-slate-800">Admin Dashboard</h1>
            </div>
            <Button onClick={onLogout} variant="outline" className="flex items-center">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        {stats && (
          <div className="stats-grid mb-8">
            <Card className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">{stats.total_students}</div>
                  <div className="stat-label">Total Students</div>
                </div>
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </Card>

            <Card className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">{stats.enrollment_paid}</div>
                  <div className="stat-label">Enrollments Paid</div>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </Card>

            <Card className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">£{stats.total_revenue}</div>
                  <div className="stat-label">Total Revenue</div>
                </div>
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
            </Card>

            <Card className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">{stats.pending_payments}</div>
                  <div className="stat-label">Pending Payments</div>
                </div>
                <AlertCircle className="w-8 h-8 text-orange-600" />
              </div>
            </Card>

            <Card className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">{stats.foundation_students}</div>
                  <div className="stat-label">Foundation Students</div>
                </div>
                <BookOpen className="w-8 h-8 text-blue-600" />
              </div>
            </Card>

            <Card className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">{stats.pro_students}</div>
                  <div className="stat-label">Pro Students</div>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-600" />
              </div>
            </Card>
          </div>
        )}

        {/* Tab Navigation */}
        <Card className="p-0 mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('students')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'students'
                  ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <Users className="w-4 h-4 mr-2 inline" />
              Student Management
            </button>
            <button
              onClick={() => setActiveTab('progress')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'progress'
                  ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <TrendingUp className="w-4 h-4 mr-2 inline" />
              Course Progress
            </button>
            <button
              onClick={() => setActiveTab('referrals')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'referrals'
                  ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <Users className="w-4 h-4 mr-2 inline" />
              Referral Codes
            </button>
            <button
              onClick={() => setActiveTab('certificates')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'certificates'
                  ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <Award className="w-4 h-4 mr-2 inline" />
              Certificates
            </button>
            <button
              onClick={() => setActiveTab('id-verification')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'id-verification'
                  ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <Shield className="w-4 h-4 mr-2 inline" />
              ID Verification
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'payments'
                  ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <DollarSign className="w-4 h-4 mr-2 inline" />
              Payment Confirmations
              {paymentConfirmations.filter(p => p.confirmation_status === 'pending').length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">
                  {paymentConfirmations.filter(p => p.confirmation_status === 'pending').length}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('author-orders')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'author-orders'
                  ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <BookOpen className="w-4 h-4 mr-2 inline" />
              Author Orders
              {authorOrders.filter(o => o.order_status === 'submitted').length > 0 && (
                <Badge className="ml-2 bg-purple-500 text-white">
                  {authorOrders.filter(o => o.order_status === 'submitted').length}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('lead-subscribers')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'lead-subscribers'
                  ? 'border-b-2 border-green-600 text-green-600 bg-green-50'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <Gift className="w-4 h-4 mr-2 inline" />
              Lead Subscribers
              {leadSubscribers.length > 0 && (
                <Badge className="ml-2 bg-green-500 text-white">
                  {leadSubscribers.length}
                </Badge>
              )}
            </button>
          </div>
        </Card>

        {activeTab === 'students' && (
          <>
            {/* Controls */}
            <Card className="p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="form-select"
              >
                <option value="all">All Status</option>
                <option value="enrolled">Enrolled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>

              <select
                value={filterCourse}
                onChange={(e) => setFilterCourse(e.target.value)}
                className="form-select"
              >
                <option value="all">All Courses</option>
                <option value="foundation">Foundation</option>
                <option value="pro">Pro</option>
              </select>
            </div>

            <Button onClick={exportToCSV} className="flex items-center">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </Card>

        {/* Students Table */}
        <Card className="table-container">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Contact</th>
                  <th>Course</th>
                  <th>Start Date</th>
                  <th>Payment Status</th>
                  <th>Total Paid</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student) => (
                  <tr key={student.id}>
                    <td>
                      <div>
                        <div className="font-medium text-slate-900">
                          {student.first_name} {student.last_name}
                        </div>
                        <div className="text-sm text-slate-500">
                          ID: {student.id.slice(0, 8)}...
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="text-sm">{student.email}</div>
                        {student.phone && (
                          <div className="text-sm text-slate-500">{student.phone}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {student.course_type ? (
                          <Badge className={student.course_type === 'pro' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                            {student.course_type === 'pro' ? 'Pro' : 'Foundation'}
                          </Badge>
                        ) : (
                          <span className="text-slate-500 text-sm">Not selected</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {student.course_start_date ? (
                        <div className="text-sm">
                          {new Date(student.course_start_date).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-slate-500 text-sm">Not selected</span>
                      )}
                    </td>
                    <td>{getPaymentStatus(student)}</td>
                    <td className="font-medium">£{student.total_paid}</td>
                    <td>
                      <Select
                        value={student.completion_status || 'enrolled'}
                        onValueChange={(value) => {
                          if (value === 'mark_paid_enrollment') {
                            markPaymentReceived(student.id, `${student.first_name} ${student.last_name}`, false, student.course_paid);
                          } else if (value === 'mark_paid_course') {
                            markPaymentReceived(student.id, `${student.first_name} ${student.last_name}`, student.enrollment_paid, false);
                          } else if (value === 'mark_paid_both') {
                            markPaymentReceived(student.id, `${student.first_name} ${student.last_name}`, false, false);
                          } else {
                            updateStudentStatus(student.id, value);
                          }
                        }}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="enrolled">Enrolled</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="emailed">Emailed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          {!student.enrollment_paid && !student.course_paid && (
                            <SelectItem value="mark_paid_both" className="text-green-600 font-semibold">💰 Mark Paid (Full)</SelectItem>
                          )}
                          {!student.enrollment_paid && (
                            <SelectItem value="mark_paid_enrollment" className="text-green-600">💰 Mark Enrollment Paid</SelectItem>
                          )}
                          {!student.course_paid && (
                            <SelectItem value="mark_paid_course" className="text-green-600">💰 Mark Course Paid</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </td>
                    <td>
                      <div className="flex space-x-2">
                        {student.completion_status !== 'completed' && student.course_paid && (
                          <Button
                            size="sm"
                            onClick={() => markStudentComplete(student.id)}
                            className="text-xs bg-green-600 hover:bg-green-700"
                          >
                            Complete
                          </Button>
                        )}
                        {(!student.enrollment_paid || !student.course_paid) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markPaymentReceived(
                              student.id, 
                              `${student.first_name} ${student.last_name}`,
                              student.enrollment_paid,
                              student.course_paid
                            )}
                            className="text-xs border-green-200 text-green-600 hover:bg-green-50"
                          >
                            <DollarSign className="w-3 h-3 mr-1" />
                            Mark Paid
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteStudent(student.id, `${student.first_name} ${student.last_name}`)}
                          className="text-xs border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredStudents.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No students found matching your criteria.</p>
            </div>
          )}
        </Card>

            {/* Summary */}
            <div className="mt-6 text-center text-sm text-slate-600">
              Showing {filteredStudents.length} of {students.length} students
            </div>
          </>
        )}

        {activeTab === 'progress' && (
          <Card className="table-container">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                Student Course Progress
              </h3>
              <p className="text-slate-600 text-sm mt-1">Track student progress through course stages</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Course Type</th>
                    <th>Progress</th>
                    <th>Completed Stages</th>
                    <th>Status</th>
                    <th>Stage Details</th>
                  </tr>
                </thead>
                <tbody>
                  {studentProgress.map((progress) => (
                    <tr key={progress.student_id}>
                      <td>
                        <div>
                          <div className="font-medium text-slate-900">
                            {progress.student_name}
                          </div>
                          <div className="text-sm text-slate-500">
                            {progress.email}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge className={progress.course_type === 'pro' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                          {progress.course_type === 'pro' ? 'Pro (9 stages)' : 'Foundation (5 stages)'}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex items-center space-x-3">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-slate-700 mb-1">
                              {Math.round(progress.progress_percentage)}%
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${progress.progress_percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="text-center">
                          <div className="text-lg font-bold text-slate-800">
                            {progress.completed_stages}/{progress.total_stages}
                          </div>
                          <div className="text-xs text-slate-500">stages</div>
                        </div>
                      </td>
                      <td>
                        {progress.progress_percentage === 100 ? (
                          <Badge className="status-badge status-completed">Completed</Badge>
                        ) : progress.completed_stages > 0 ? (
                          <Badge className="status-badge status-in-progress">In Progress</Badge>
                        ) : (
                          <Badge className="status-badge">Not Started</Badge>
                        )}
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {(progress.course_type === 'foundation' ? progress.foundation_progress : progress.pro_progress).map((completed, index) => (
                            <div
                              key={index}
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                completed 
                                  ? 'bg-green-100 text-green-800 border border-green-300' 
                                  : 'bg-slate-100 text-slate-500 border border-slate-300'
                              }`}
                              title={`Stage ${index + 1}: ${completed ? 'Completed' : 'Not completed'}`}
                            >
                              {completed ? '✓' : index + 1}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {studentProgress.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No student progress data available.</p>
                <p className="text-sm mt-2">Students must complete course payment to appear here.</p>
              </div>
            )}
          </Card>
        )}

        {activeTab === 'referrals' && (
          <Card className="table-container">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                <Users className="w-5 h-5 mr-2 text-blue-600" />
                Student Referral Codes
              </h3>
              <p className="text-slate-600 text-sm mt-1">View and manage student referral codes</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Email</th>
                    <th>Referral Code</th>
                    <th>Status</th>
                    <th>Enrollment</th>
                    <th>Course</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.id}>
                      <td>
                        <div className="font-medium text-slate-900">
                          {student.first_name} {student.last_name}
                        </div>
                      </td>
                      <td>
                        <div className="text-slate-600">{student.email}</div>
                      </td>
                      <td>
                        <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                          {student.referral_code || 'N/A'}
                        </code>
                      </td>
                      <td>
                        <Badge className={
                          student.enrollment_paid && student.course_paid
                            ? 'bg-green-100 text-green-800'
                            : student.enrollment_paid
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-slate-100 text-slate-600'
                        }>
                          {student.enrollment_paid && student.course_paid ? 'Fully Enrolled' :
                           student.enrollment_paid ? 'Enrollment Paid' : 'Not Enrolled'}
                        </Badge>
                      </td>
                      <td>
                        <span className={student.enrollment_paid ? 'text-green-600' : 'text-slate-400'}>
                          {student.enrollment_paid ? '✅ Paid' : '❌ Unpaid'}
                        </span>
                      </td>
                      <td>
                        <span className={student.course_paid ? 'text-green-600' : 'text-slate-400'}>
                          {student.course_paid ? `✅ ${student.course_type || 'Unknown'}` : '❌ Unpaid'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {students.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No students found.</p>
              </div>
            )}
          </Card>
        )}

        {activeTab === 'certificates' && (
          <Card className="table-container">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                <Award className="w-5 h-5 mr-2 text-blue-600" />
                Certificate Management
              </h3>
              <p className="text-slate-600 text-sm mt-1">Review and activate student certificates</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Certificate ID</th>
                    <th>Student</th>
                    <th>Course</th>
                    <th>Completion Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates.map((cert) => (
                    <tr key={cert.certificate_id}>
                      <td>
                        <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                          {cert.certificate_id}
                        </code>
                      </td>
                      <td>
                        <div>
                          <div className="font-medium text-slate-900">
                            {cert.student_name}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge className={cert.course_type === 'pro' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                          {cert.course_type === 'pro' ? 'Pro Course' : 'Foundation Course'}
                        </Badge>
                      </td>
                      <td>
                        <div className="text-sm text-slate-600">
                          {new Date(cert.completion_date).toLocaleDateString('en-GB')}
                        </div>
                      </td>
                      <td>
                        <Badge className={
                          cert.activated 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-orange-100 text-orange-800'
                        }>
                          {cert.activated ? 'Activated' : 'Pending'}
                        </Badge>
                      </td>
                      <td>
                        {!cert.activated && (
                          <Button
                            onClick={() => activateCertificate(cert.certificate_id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            size="sm"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Activate
                          </Button>
                        )}
                        {cert.activated && (
                          <span className="text-green-600 text-sm font-medium">
                            ✅ Ready for Download
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {certificates.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No certificates requested yet.</p>
                <p className="text-sm mt-2">Certificates appear here when students complete their courses.</p>
              </div>
            )}
          </Card>
        )}

        {activeTab === 'id-verification' && (
          <Card className="table-container">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-blue-600" />
                ID Verification Management
              </h3>
              <p className="text-slate-600 text-sm mt-1">Review and approve student ID documents</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Course</th>
                    <th>Upload Date</th>
                    <th>ID Document</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {idVerifications.map((verification) => (
                    <tr key={verification.student_id}>
                      <td>
                        <div>
                          <div className="font-medium text-slate-900">
                            {verification.student_name}
                          </div>
                          <div className="text-sm text-slate-600">
                            {verification.email}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge 
                          className={
                            verification.course_type === 'pro' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }
                        >
                          {verification.course_type === 'pro' ? 'Pro' : 'Foundation'}
                        </Badge>
                      </td>
                      <td className="text-sm text-slate-600">
                        {new Date(verification.uploaded_date).toLocaleDateString()}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            // Open ID document in new tab
                            const newWindow = window.open();
                            newWindow.document.write(`
                              <html>
                                <head><title>ID Document - ${verification.student_name}</title></head>
                                <body style="margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f1f5f9;">
                                  <img src="${verification.id_document_url}" style="max-width: 90%; max-height: 90%; box-shadow: 0 4px 6px rgba(0,0,0,0.1);" />
                                </body>
                              </html>
                            `);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View ID
                        </Button>
                      </td>
                      <td>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            onClick={() => reviewIdVerification(verification.student_id, 'approved')}
                            disabled={reviewingId === verification.student_id}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            {reviewingId === verification.student_id ? (
                              <div className="spinner mr-1"></div>
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-1" />
                            )}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const reason = prompt('Please provide a reason for rejection:');
                              if (reason) {
                                reviewIdVerification(verification.student_id, 'rejected', reason);
                              }
                            }}
                            disabled={reviewingId === verification.student_id}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {idVerifications.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No pending ID verifications.</p>
                <p className="text-sm mt-2">ID documents appear here when students upload them for review.</p>
              </div>
            )}
          </Card>
        )}


        {activeTab === 'payments' && (
          <Card className="card">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-slate-800">Payment Confirmations</h2>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                  {paymentConfirmations.filter(p => p.confirmation_status === 'pending').length} Pending
                </Badge>
                <Badge variant="outline" className="text-green-600 border-green-600">
                  {paymentConfirmations.filter(p => p.confirmation_status === 'verified').length} Verified
                </Badge>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Student Email</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Payer Name</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Package</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Amount</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Date Paid</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Reference</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Status</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentConfirmations.map((confirmation) => (
                    <tr key={confirmation.id} className="border-b hover:bg-slate-50">
                      <td className="p-4">
                        <div className="text-sm font-medium text-slate-900">{confirmation.student_email}</div>
                        {confirmation.student_name && (
                          <div className="text-xs text-slate-500">{confirmation.student_name}</div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-slate-900">{confirmation.payer_name}</div>
                        {confirmation.payer_email && (
                          <div className="text-xs text-slate-500">{confirmation.payer_email}</div>
                        )}
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="text-xs">
                          {confirmation.package_type === 'enrollment' && 'Enrollment Only'}
                          {confirmation.package_type === 'foundation' && 'Foundation Course'}
                          {confirmation.package_type === 'pro' && 'Pro Course'}
                          {confirmation.package_type === 'enrollment_foundation' && 'Enrollment + Foundation'}
                          {confirmation.package_type === 'enrollment_pro' && 'Enrollment + Pro'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="text-sm font-semibold text-emerald-600">£{confirmation.amount}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-slate-600">
                          {new Date(confirmation.paid_at).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-slate-500">
                          {new Date(confirmation.paid_at).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-xs text-slate-600">
                          {confirmation.reference || '-'}
                        </div>
                      </td>
                      <td className="p-4">
                        {confirmation.confirmation_status === 'pending' && (
                          <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                        )}
                        {confirmation.confirmation_status === 'verified' && (
                          <Badge className="bg-green-100 text-green-800">Verified</Badge>
                        )}
                        {confirmation.confirmation_status === 'rejected' && (
                          <Badge className="bg-red-100 text-red-800">Rejected</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        {confirmation.confirmation_status === 'pending' && (
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              onClick={() => verifyPaymentConfirmation(
                                confirmation.id,
                                confirmation.package_type === 'enrollment' ? 'verify_enrollment' :
                                confirmation.package_type.includes('enrollment_') ? 'verify_full' : 'verify_course',
                                confirmation.student_name || confirmation.student_email
                              )}
                              className="text-xs bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verify
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => verifyPaymentConfirmation(
                                confirmation.id,
                                'reject',
                                confirmation.student_name || confirmation.student_email
                              )}
                              className="text-xs border-red-200 text-red-600 hover:bg-red-50"
                            >
                              <X className="w-3 h-3 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                        {confirmation.confirmation_status !== 'pending' && (
                          <div className="text-xs text-slate-500">
                            {confirmation.confirmed_status === 'verified' ? '✓ Verified' : '✗ Rejected'}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {paymentConfirmations.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No payment confirmations yet.</p>
                <p className="text-sm mt-2">Student payment confirmations will appear here for review.</p>
              </div>
            )}
          </Card>
        )}

        {activeTab === 'author-orders' && (
          <Card className="card">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-slate-800">Author Video Review Orders</h2>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                  {authorOrders.filter(o => o.order_status === 'pending_details').length} Pending Details
                </Badge>
                <Badge variant="outline" className="text-blue-600 border-blue-600">
                  {authorOrders.filter(o => o.order_status === 'submitted').length} Submitted
                </Badge>
                <Badge variant="outline" className="text-purple-600 border-purple-600">
                  {authorOrders.filter(o => o.order_status === 'in_progress').length} In Progress
                </Badge>
                <Badge variant="outline" className="text-green-600 border-green-600">
                  {authorOrders.filter(o => o.order_status === 'completed').length} Completed
                </Badge>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Author</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Package</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Price</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Books</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Status</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Created</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {authorOrders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-slate-50">
                      <td className="p-4">
                        <div className="text-sm font-medium text-slate-900">
                          {order.author_name || order.author_email}
                        </div>
                        <div className="text-xs text-slate-500">{order.author_email}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-slate-900">{order.package_title}</div>
                        <div className="text-xs text-slate-500">ID: {order.package_id}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm font-semibold text-emerald-600">£{order.package_price}</div>
                      </td>
                      <td className="p-4">
                        {order.books && order.books.length > 0 ? (
                          <div className="space-y-1">
                            {order.books.map((book, idx) => (
                              <div key={idx} className="text-xs">
                                <span className="font-medium text-slate-800">{book.title}</span>
                                {book.amazon_link && (
                                  <a
                                    href={book.amazon_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-1 text-purple-600 hover:underline"
                                  >
                                    [Link]
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">No books yet</span>
                        )}
                      </td>
                      <td className="p-4">
                        {order.order_status === 'pending_details' && (
                          <Badge className="bg-yellow-100 text-yellow-800">Pending Details</Badge>
                        )}
                        {order.order_status === 'submitted' && (
                          <Badge className="bg-blue-100 text-blue-800">Submitted</Badge>
                        )}
                        {order.order_status === 'in_progress' && (
                          <Badge className="bg-purple-100 text-purple-800">In Progress</Badge>
                        )}
                        {order.order_status === 'completed' && (
                          <Badge className="bg-green-100 text-green-800">Completed</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-slate-600">
                          {new Date(order.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="p-4">
                        {order.order_status === 'submitted' && (
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/admin/author-orders/${order.id}/status?new_status=in_progress`);
                                toast.success('Order marked as In Progress');
                                fetchDashboardData();
                              } catch (error) {
                                toast.error('Failed to update order status');
                              }
                            }}
                            className="text-xs bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            Start Production
                          </Button>
                        )}
                        {order.order_status === 'in_progress' && (
                          <Button
                            size="sm"
                            onClick={() => {
                              const videoUrl = prompt('Enter the video URL (MP4):');
                              const youtubeLink = prompt('Enter the YouTube link (optional):');
                              if (videoUrl) {
                                axios.put(`${API}/admin/author-orders/${order.id}/deliver?video_url=${encodeURIComponent(videoUrl)}${youtubeLink ? '&youtube_link=' + encodeURIComponent(youtubeLink) : ''}`)
                                  .then(() => {
                                    toast.success('Video delivered successfully!');
                                    fetchDashboardData();
                                  })
                                  .catch(() => toast.error('Failed to deliver video'));
                              }
                            }}
                            className="text-xs bg-green-600 hover:bg-green-700 text-white"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Deliver Video
                          </Button>
                        )}
                        {order.order_status === 'completed' && order.video_url && (
                          <a
                            href={order.video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-purple-600 hover:underline flex items-center"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View Video
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {authorOrders.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No author orders yet.</p>
                <p className="text-sm mt-2">Author video review orders will appear here.</p>
              </div>
            )}
          </Card>
        )}

        {activeTab === 'lead-subscribers' && (
          <Card className="overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-slate-800">Lead Magnet Subscribers</h2>
                <p className="text-sm text-slate-500 mt-1">People who downloaded the free guide</p>
              </div>
              <Button
                onClick={() => {
                  const csvContent = "Name,Email,Subscribed Date,Source\n" + 
                    leadSubscribers.map(s => 
                      `"${s.name}","${s.email}","${new Date(s.subscribed_at).toLocaleDateString()}","${s.source || 'support_page'}"`
                    ).join("\n");
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `lead-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                  toast.success('CSV exported successfully!');
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={leadSubscribers.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-4 px-6 font-medium text-slate-600">Name</th>
                    <th className="text-left py-4 px-6 font-medium text-slate-600">Email</th>
                    <th className="text-left py-4 px-6 font-medium text-slate-600">Subscribed Date</th>
                    <th className="text-left py-4 px-6 font-medium text-slate-600">Source</th>
                    <th className="text-left py-4 px-6 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leadSubscribers.map((subscriber, index) => (
                    <tr key={subscriber.id || index} className="hover:bg-slate-50">
                      <td className="py-4 px-6">
                        <div className="font-medium text-slate-800">{subscriber.name}</div>
                      </td>
                      <td className="py-4 px-6">
                        <a 
                          href={`mailto:${subscriber.email}`}
                          className="text-blue-600 hover:underline flex items-center"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          {subscriber.email}
                        </a>
                      </td>
                      <td className="py-4 px-6 text-slate-600">
                        {new Date(subscriber.subscribed_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-4 px-6">
                        <Badge className="bg-green-100 text-green-800">
                          {subscriber.source === 'support_page_free_guide' ? 'Free Guide' : subscriber.source || 'Support Page'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6">
                        <a
                          href={`mailto:${subscriber.email}?subject=Following up on your Free Guide`}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Send Email
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {leadSubscribers.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Gift className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No lead subscribers yet.</p>
                <p className="text-sm mt-2">People who download the free guide will appear here.</p>
              </div>
            )}
          </Card>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;